import type { FileIconType, MailItem, MeetingNoteItem, MessageItem, VaultFileItem, VaultFolderNode } from "@/types/vault.types";
import { enrichDemoFiles } from "@/lib/vault/vaultFileUtils";

const mimeMap: Record<FileIconType, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  image: "image/png",
  link: "text/html",
  text: "text/plain",
};

const extMap: Record<FileIconType, string> = {
  pdf: ".pdf",
  docx: ".docx",
  xlsx: ".xlsx",
  image: ".png",
  link: "",
  text: ".txt",
};

function mockFile(id: string, name: string, fileIcon: FileIconType, date: string): VaultFileItem {
  return {
    id,
    name,
    fileIcon,
    date,
    sizeBytes: 0,
    sizeFormatted: "—",
    fileType: mimeMap[fileIcon] || "application/octet-stream",
    extension: extMap[fileIcon] || "",
    createdAt: "",
    updatedAt: "",
    createdAtFormatted: "",
    updatedAtFormatted: date,
  };
}

export const vaultMailItems: MailItem[] = [
  { id: "m1", subject: "RE: Recording Agreement - Final Terms", from: "Sarah Chen (Sony Music)", preview: "Hi, I've reviewed the latest redline and have a few comments on Section 4.2 regarding territorial rights...", date: "Mar 8, 2026", hasAttachment: true, unread: true },
  { id: "m2", subject: "Nike Campaign Deliverables Update", from: "Marcus Johnson (Nike)", preview: "The creative team has approved the initial concepts. Please find attached the updated deliverable schedule...", date: "Mar 7, 2026", hasAttachment: true, unread: true },
  { id: "m3", subject: "Sync License - Netflix Series", from: "David Park (Netflix Legal)", preview: "Following up on our call, we'd like to proceed with the sync placement for episodes 3 and 7...", date: "Mar 6, 2026", hasAttachment: false, unread: false },
  { id: "m4", subject: "Q1 Royalty Statement Ready", from: "Accounting (Warner Chappell)", preview: "Your Q1 2026 royalty statement is now available for review. Total mechanical royalties for the period...", date: "Mar 5, 2026", hasAttachment: true, unread: false },
  { id: "m5", subject: "Tour Rider - Spring Dates", from: "Lisa Wong (Live Nation)", preview: "Attached is the updated rider for the spring tour dates. Please confirm the technical requirements...", date: "Mar 3, 2026", hasAttachment: true, unread: false },
  { id: "m6", subject: "Collaboration Agreement Draft", from: "James Rivera (Artist Mgmt)", preview: "Here's the initial draft for the featured artist collaboration. Let me know if the split terms work...", date: "Mar 1, 2026", hasAttachment: false, unread: false },
];

export const vaultMeetingNotes: MeetingNoteItem[] = [
  { id: "mt1", title: "Sony Partnership Review - Q1 Goals", date: "Mar 7, 2026", duration: "45 min", attendees: ["Sarah Chen", "Mike Torres", "You"], source: "Google Meet" },
  { id: "mt2", title: "Nike Campaign Kickoff", date: "Mar 5, 2026", duration: "60 min", attendees: ["Marcus Johnson", "Creative Team", "You"], source: "Zoom" },
  { id: "mt3", title: "Legal Review - Sync Licenses", date: "Mar 3, 2026", duration: "30 min", attendees: ["David Park", "You"], source: "Google Meet" },
  { id: "mt4", title: "Tour Planning - Spring 2026", date: "Feb 28, 2026", duration: "90 min", attendees: ["Lisa Wong", "Production Team", "You"], source: "Zoom" },
  { id: "mt5", title: "Publishing Admin Check-in", date: "Feb 25, 2026", duration: "25 min", attendees: ["Rachel Kim", "You"], source: "Teams" },
];

export const vaultMessages: MessageItem[] = [
  { id: "ms1", channel: "#deal-sony-partnership", sender: "Sarah Chen", preview: "Just uploaded the latest revision to the vault", date: "Mar 8, 2026", source: "Slack" },
  { id: "ms2", channel: "#legal-review", sender: "Mike Torres", preview: "Can someone review the Nike NDA before EOD?", date: "Mar 7, 2026", source: "Slack" },
  { id: "ms3", channel: "#tour-planning", sender: "Lisa Wong", preview: "Venue confirmed for the March 22 date in LA", date: "Mar 6, 2026", source: "Slack" },
  { id: "ms4", channel: "#general", sender: "Admin Bot", preview: "Reminder: Q1 royalty reports are due by March 15", date: "Mar 5, 2026", source: "Slack" },
  { id: "ms5", channel: "#deal-netflix-sync", sender: "David Park", preview: "Netflix approved the final sync placement terms", date: "Mar 4, 2026", source: "Teams" },
];

export const vaultEqualiqRoot: VaultFolderNode = {
  id: "eq-root",
  name: "EQUAL-IQ",
  children: [
    {
      id: "eq-templates",
      name: "Templates",
      children: [
        {
          id: "eq-templates-recording",
          name: "Recording",
          children: [
            mockFile("tf1", "Recording Agreement Template.docx", "docx", "Dec 20, 2024"),
            mockFile("tf1b", "Master License Template.docx", "docx", "Dec 18, 2024"),
            mockFile("tf1c", "Producer Agreement - Work for Hire.docx", "docx", "Dec 12, 2024"),
          ],
        },
        {
          id: "eq-templates-brand",
          name: "Brand & Endorsement",
          children: [
            mockFile("tf5", "Brand Endorsement Template.docx", "docx", "Jan 12, 2026"),
            mockFile("tf6", "Collaboration Term Sheet Template.docx", "docx", "Jan 8, 2026"),
            mockFile("tf7", "Exclusivity Rider Template.docx", "docx", "Dec 22, 2025"),
          ],
        },
        mockFile("tf2", "NDA Template.docx", "docx", "Dec 15, 2024"),
        mockFile("tf3", "Invoice Template.xlsx", "xlsx", "Dec 10, 2024"),
        mockFile("tf4", "Publishing Agreement Template.docx", "docx", "Nov 28, 2024"),
        mockFile("tf8", "Touring Rights Agreement Template.docx", "docx", "Nov 15, 2024"),
        mockFile("tf9", "Sync License Template.docx", "docx", "Nov 8, 2024"),
        mockFile("tf10", "Sports Endorsement Template.docx", "docx", "Oct 30, 2024"),
      ],
    },
    {
      id: "eq-brand-deals",
      name: "Brand Deals",
      children: [
        {
          id: "eq-brand-nike",
          name: "Nike",
          children: [
            mockFile("bd1", "Savage x Fenty x Nike - Term Sheet.pdf", "pdf", "Apr 27, 2026"),
            mockFile("bd2", "LeBron Royalty Amendment - Redline.docx", "docx", "Apr 25, 2026"),
            mockFile("bd3", "Kobe Brand Reacquisition - LOI.pdf", "pdf", "Apr 20, 2026"),
            mockFile("bd4", "Nike Exclusivity Matrix.xlsx", "xlsx", "Apr 18, 2026"),
          ],
        },
        {
          id: "eq-brand-mcd",
          name: "McDonald's",
          children: [
            mockFile("bd5", "Travis Scott Phase 2 - Campaign Brief.pdf", "pdf", "Apr 22, 2026"),
            mockFile("bd6", "IP Ownership Clause - Markup.docx", "docx", "Apr 19, 2026"),
          ],
        },
        mockFile("bd7", "Wilson Legacy Deal - Summary.pdf", "pdf", "Apr 15, 2026"),
        mockFile("bd8", "Brand Deal Comparison Matrix.xlsx", "xlsx", "Apr 10, 2026"),
      ],
    },
    {
      id: "eq-active-deals",
      name: "Active Deals",
      children: [
        {
          id: "eq-deal-jayz",
          name: "Jay-Z Catalog",
          children: [
            mockFile("ad1", "Roc Nation - Master License Term Sheet.pdf", "pdf", "Apr 27, 2026"),
            mockFile("ad2", "Tidal Windowing Clarification - Redline.docx", "docx", "Apr 27, 2026"),
            mockFile("ad3", "Reversion Clause - Year 10 Exhibit.pdf", "pdf", "Apr 24, 2026"),
            mockFile("ad4", "Streaming Revenue Split Model.xlsx", "xlsx", "Apr 21, 2026"),
          ],
        },
        {
          id: "eq-deal-beyonce",
          name: "Renaissance II Tour",
          children: [
            mockFile("ad5", "Parkwood - Touring Rights Agreement.pdf", "pdf", "Apr 14, 2026"),
            mockFile("ad6", "Venue Guarantee Schedule.xlsx", "xlsx", "Apr 12, 2026"),
          ],
        },
        mockFile("ad7", "Deal Pipeline Snapshot - Apr 2026.xlsx", "xlsx", "Apr 28, 2026"),
        mockFile("ad8", "Open Items & Next Steps.docx", "docx", "Apr 26, 2026"),
      ],
    },
    {
      id: "eq-legal",
      name: "Legal Documents",
      children: [
        {
          id: "eq-legal-ndas",
          name: "NDAs",
          children: [
            mockFile("lf2", "NDA - Sony Music.pdf", "pdf", "Jan 8, 2025"),
            mockFile("lf3", "NDA - Netflix.pdf", "pdf", "Jan 3, 2025"),
            mockFile("lf5", "NDA - Roc Nation.pdf", "pdf", "Apr 20, 2026"),
            mockFile("lf6", "NDA - Parkwood Entertainment.pdf", "pdf", "Apr 15, 2026"),
            mockFile("lf7", "NDA - Klutch Sports.pdf", "pdf", "Apr 12, 2026"),
            mockFile("lf8", "NDA - Fenty Corp.pdf", "pdf", "Apr 8, 2026"),
          ],
        },
        {
          id: "eq-legal-redlines",
          name: "Redlines",
          children: [
            mockFile("lf9", "Jay-Z Catalog - Redline v3.docx", "docx", "Apr 27, 2026"),
            mockFile("lf10", "Savage x Fenty Nike - Redline v2.docx", "docx", "Apr 25, 2026"),
            mockFile("lf11", "LeBron Amendment - Counter Redline.docx", "docx", "Apr 24, 2026"),
          ],
        },
        mockFile("lf1", "Standard Terms & Conditions.pdf", "pdf", "Jan 5, 2025"),
        mockFile("lf4", "https://equaliq.app/legal/privacy", "link", "Dec 1, 2024"),
        mockFile("lf12", "Mutual NDA - Short Form.pdf", "pdf", "Mar 30, 2026"),
        mockFile("lf13", "Signature Block Standards.pdf", "pdf", "Mar 15, 2026"),
      ],
    },
    {
      id: "eq-financial",
      name: "Financial",
      children: [
        mockFile("ff1", "Q4 2024 Royalty Statement.xlsx", "xlsx", "Jan 10, 2025"),
        mockFile("ff2", "Payment Schedule 2025.xlsx", "xlsx", "Jan 5, 2025"),
        mockFile("ff3", "Tax Documents 2024.pdf", "pdf", "Dec 28, 2024"),
        mockFile("ff4", "Q1 2026 Commission Summary.xlsx", "xlsx", "Apr 28, 2026"),
        mockFile("ff5", "Deal Value Tracker - Pipeline.xlsx", "xlsx", "Apr 26, 2026"),
        mockFile("ff6", "Advance Recoupment Schedule.xlsx", "xlsx", "Apr 22, 2026"),
        mockFile("ff7", "Guaranteed Minimums by Deal.pdf", "pdf", "Apr 18, 2026"),
        mockFile("ff8", "1099 Prep - Client Roster.xlsx", "xlsx", "Apr 10, 2026"),
      ],
    },
    {
      id: "eq-press",
      name: "Press & Marketing",
      children: [
        mockFile("pm1", "Artist Press Kit - Template.pdf", "pdf", "Mar 20, 2026"),
        mockFile("pm2", "Campaign Deliverables Checklist.xlsx", "xlsx", "Mar 15, 2026"),
        mockFile("pm3", "Social Usage Rights Guide.pdf", "pdf", "Mar 8, 2026"),
        mockFile("pm4", "Brand Pitch Deck - Q2 2026.pdf", "pdf", "Feb 28, 2026"),
      ],
    },
  ],
};

export const vaultGdriveRoot: VaultFolderNode = {
  id: "gd-root",
  name: "Google Drive",
  children: [
    {
      id: "gd-contracts",
      name: "Contracts",
      children: [
        {
          id: "gd-contracts-active",
          name: "Active",
          children: [
            mockFile("gd1", "Contract Review Notes.docx", "docx", "Mar 6, 2026"),
            mockFile("gd1b", "Jay-Z Term Sheet - Working Copy.docx", "docx", "Apr 27, 2026"),
            mockFile("gd1c", "Fenty Nike Collab - Comments.docx", "docx", "Apr 25, 2026"),
          ],
        },
        {
          id: "gd-contracts-archive",
          name: "Archive",
          children: [
            mockFile("gd2", "Deal Comparison Matrix.xlsx", "xlsx", "Mar 4, 2026"),
            mockFile("gd2b", "2025 Closed Deals - Summary.xlsx", "xlsx", "Jan 15, 2026"),
          ],
        },
      ],
    },
    {
      id: "gd-reports",
      name: "Reports",
      children: [
        mockFile("gd3", "Artist Bio & Press Kit.pdf", "pdf", "Feb 28, 2026"),
        mockFile("gd4", "Revenue Forecast 2026.xlsx", "xlsx", "Feb 20, 2026"),
        mockFile("gd5", "Pipeline Weekly - Apr 28.xlsx", "xlsx", "Apr 28, 2026"),
        mockFile("gd6", "Client Relationship Scores.pdf", "pdf", "Apr 20, 2026"),
      ],
    },
    {
      id: "gd-deal-comparison",
      name: "Deal Comparisons",
      children: [
        mockFile("gd7", "Offer Comparison - Nike Collab.xlsx", "xlsx", "Apr 26, 2026"),
        mockFile("gd8", "Term Sheet Diff - LeBron Amendment.pdf", "pdf", "Apr 24, 2026"),
      ],
    },
  ],
};

export const vaultDropboxRoot: VaultFolderNode = {
  id: "db-root",
  name: "Dropbox",
  children: [
    {
      id: "db-sessions",
      name: "Sessions",
      children: [
        mockFile("db1", "Master Recordings - Session Files.pdf", "pdf", "Mar 5, 2026"),
        mockFile("db1b", "Renaissance II - Rehearsal Schedule.pdf", "pdf", "Apr 12, 2026"),
      ],
    },
    {
      id: "db-artwork",
      name: "Artwork",
      children: [
        mockFile("db2", "Album Artwork Final.pdf", "image", "Mar 2, 2026"),
        mockFile("db2b", "Brand Campaign Assets - Nike.png", "image", "Apr 18, 2026"),
        mockFile("db2c", "Tour Poster - Renaissance II.png", "image", "Apr 10, 2026"),
      ],
    },
    {
      id: "db-signed",
      name: "Signed Copies",
      children: [
        mockFile("db4", "Serena Wilson Legacy - Executed.pdf", "pdf", "Apr 15, 2026"),
        mockFile("db5", "Renaissance II Tour - Signed.pdf", "pdf", "Apr 8, 2026"),
      ],
    },
    mockFile("db3", "Licensing Agreements Archive.pdf", "pdf", "Feb 15, 2026"),
    mockFile("db6", "Vault Export - All NDAs.zip", "pdf", "Apr 1, 2026"),
  ],
};

export const vaultDealFilesMap: Record<string, VaultFileItem[]> = {
  DEMO_DEAL_RECORDING: [
    mockFile("f1", "Master Agreement.pdf", "pdf", "Jan 7, 2025"),
    mockFile("f2", "Amendment #1.pdf", "pdf", "Jan 5, 2025"),
    mockFile("f3", "Side Letter - Royalties.pdf", "pdf", "Jan 3, 2025"),
    mockFile("f4", "Payment Schedule.xlsx", "xlsx", "Dec 15, 2024"),
  ],
  "deal-1": [
    mockFile("f5", "Recording Partnership Agreement.pdf", "pdf", "Jan 6, 2025"),
    mockFile("f6", "Royalty Schedule.xlsx", "xlsx", "Jan 4, 2025"),
    mockFile("f7", "https://sony.com/partnership-terms", "link", "Jan 2, 2025"),
    mockFile("f8", "Territory Rights Exhibit.pdf", "pdf", "Dec 28, 2024"),
    mockFile("f9", "Distribution Agreement.pdf", "pdf", "Dec 20, 2024"),
    mockFile("f10", "Revenue Split Breakdown.xlsx", "xlsx", "Dec 15, 2024"),
  ],
  "deal-2": [
    mockFile("f11", "Sync License Agreement.pdf", "pdf", "Jan 5, 2025"),
    mockFile("f12", "Usage Rights Exhibit.pdf", "pdf", "Dec 20, 2024"),
  ],
  "deal-3": [
    mockFile("f13", "Touring Services Agreement.pdf", "pdf", "Jan 3, 2025"),
  ],
  "deal-4": [
    mockFile("f14", "Distribution Placement Agreement.pdf", "pdf", "Dec 20, 2024"),
    mockFile("f15", "Playlist Metrics Report.xlsx", "xlsx", "Dec 18, 2024"),
    mockFile("f16", "https://spotify.com/placement-terms", "link", "Dec 10, 2024"),
  ],
  "deal-5": [
    mockFile("f17", "Brand Endorsement Contract.pdf", "pdf", "Jan 8, 2025"),
    mockFile("f18", "Campaign Brief.pdf", "pdf", "Jan 5, 2025"),
    mockFile("f19", "Social Media Deliverables.docx", "docx", "Jan 3, 2025"),
    mockFile("f20", "Image Usage Rights.pdf", "pdf", "Dec 28, 2024"),
    mockFile("f21", "Payment Terms.xlsx", "xlsx", "Dec 20, 2024"),
    mockFile("f22", "https://nike.com/ambassador-terms", "link", "Dec 15, 2024"),
    mockFile("f23", "NDA - Nike.pdf", "pdf", "Nov 20, 2024"),
    mockFile("f24", "Talent Release Form.pdf", "pdf", "Nov 15, 2024"),
  ],
  "deal-6": [
    mockFile("f25", "Publishing Administration Agreement.pdf", "pdf", "Dec 15, 2024"),
    mockFile("f26", "Song Catalog List.xlsx", "xlsx", "Dec 10, 2024"),
  ],
  "deal-7": [
    mockFile("f27", "Featured Artist Collaboration Agreement.pdf", "pdf", "Jan 2, 2025"),
    mockFile("f28", "Split Sheet.xlsx", "xlsx", "Dec 28, 2024"),
    mockFile("f29", "NDA - Collaboration.pdf", "pdf", "Dec 20, 2024"),
  ],
  "deal-8": [
    mockFile("f30", "Merchandise License Agreement.pdf", "pdf", "Nov 30, 2024"),
    mockFile("f31", "Product Catalog.xlsx", "xlsx", "Nov 25, 2024"),
    mockFile("f32", "Brand Guidelines.pdf", "pdf", "Nov 20, 2024"),
    mockFile("f33", "Revenue Projections.xlsx", "xlsx", "Nov 15, 2024"),
    mockFile("f34", "https://hottopic.com/vendor-portal", "link", "Nov 10, 2024"),
  ],
  "deal-9": [
    mockFile("f35", "Film Score Commission Agreement.pdf", "pdf", "Jan 4, 2025"),
    mockFile("f36", "Delivery Schedule.xlsx", "xlsx", "Dec 28, 2024"),
    mockFile("f37", "Creative Brief.pdf", "pdf", "Dec 20, 2024"),
    mockFile("f38", "NDA - Film Project.pdf", "pdf", "Dec 10, 2024"),
  ],
  "deal-10": [
    mockFile("f39", "Artist Management Agreement.pdf", "pdf", "Oct 15, 2024"),
    mockFile("f40", "Commission Structure.xlsx", "xlsx", "Oct 10, 2024"),
  ],
  "ari-pipeline-1": [
    mockFile("ap1", "Roc Nation - Master License Term Sheet.pdf", "pdf", "Apr 27, 2026"),
    mockFile("ap2", "Tidal Windowing - Redline v3.docx", "docx", "Apr 27, 2026"),
    mockFile("ap3", "Reversion Clause Exhibit.pdf", "pdf", "Apr 24, 2026"),
    mockFile("ap4", "Streaming Revenue Model.xlsx", "xlsx", "Apr 21, 2026"),
    mockFile("ap5", "Catalog Schedule - Annex A.pdf", "pdf", "Apr 18, 2026"),
  ],
  "ari-pipeline-2": [
    mockFile("ap6", "Savage x Fenty x Nike - Term Sheet.pdf", "pdf", "Apr 26, 2026"),
    mockFile("ap7", "Revenue Split 60-40 - Model.xlsx", "xlsx", "Apr 25, 2026"),
    mockFile("ap8", "Exclusivity Clause - Markup.docx", "docx", "Apr 23, 2026"),
    mockFile("ap9", "Guaranteed Units - 2M Exhibit.pdf", "pdf", "Apr 20, 2026"),
  ],
  "ari-pipeline-3": [
    mockFile("ap10", "LeBron Royalty Amendment.pdf", "pdf", "Apr 25, 2026"),
    mockFile("ap11", "Equity Conversion Terms.docx", "docx", "Apr 24, 2026"),
    mockFile("ap12", "Nike Lifetime Deal - Summary.pdf", "pdf", "Apr 22, 2026"),
  ],
  "ari-pipeline-4": [
    mockFile("ap13", "Kobe Brand Reacquisition - LOI.pdf", "pdf", "Apr 20, 2026"),
    mockFile("ap14", "Mamba Sports - Term Outline.docx", "docx", "Apr 18, 2026"),
  ],
  "ari-pipeline-5": [
    mockFile("ap15", "Renaissance II Touring Rights.pdf", "pdf", "Apr 14, 2026"),
    mockFile("ap16", "Venue Guarantee Schedule.xlsx", "xlsx", "Apr 12, 2026"),
    mockFile("ap17", "Parkwood - Signed Agreement.pdf", "pdf", "Apr 8, 2026"),
  ],
  "ari-pipeline-6": [
    mockFile("ap18", "Travis x McDonald's Phase 2.pdf", "pdf", "Apr 22, 2026"),
    mockFile("ap19", "IP Ownership Clause.docx", "docx", "Apr 19, 2026"),
    mockFile("ap20", "Campaign Deliverables.xlsx", "xlsx", "Apr 16, 2026"),
  ],
  "ari-pipeline-7": [
    mockFile("ap21", "Serena Wilson Legacy Deal.pdf", "pdf", "Apr 15, 2026"),
    mockFile("ap22", "WTA Partnership Terms.pdf", "pdf", "Apr 12, 2026"),
  ],
  "ari-pipeline-8": [
    mockFile("ap23", "Atlas Ventures - DD Checklist.xlsx", "xlsx", "Apr 10, 2026"),
    mockFile("ap24", "Series B Term Sheet.pdf", "pdf", "Apr 8, 2026"),
  ],
  "ari-suggested-1": [
    mockFile("as1", "Inbox Thread Export - Term Sheet.pdf", "pdf", "Apr 27, 2026"),
    mockFile("as2", "AI Summary - Deal Notes.docx", "docx", "Apr 27, 2026"),
  ],
  "ari-suggested-2": [
    mockFile("as3", "Suggested Deal - Initial Terms.pdf", "pdf", "Apr 26, 2026"),
  ],
};

enrichDemoFiles(vaultEqualiqRoot, "eq");
enrichDemoFiles(vaultGdriveRoot, "gdrive");
enrichDemoFiles(vaultDropboxRoot, "dropbox");
for (const files of Object.values(vaultDealFilesMap)) {
  files.forEach((f, i) => {
    f.sourceId = "eq";
    f.starred = i % 4 === 0;
    f.shared = i % 2 === 0;
  });
}
